/**
 * QA Pre-Submission Panel
 * 
 * Comprehensive pre-submission review showing all checklist categories,
 * completion percentages, validation status, and blocking issues.
 */

import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Badge } from '../UI/Badge';
import { LoadingSpinner } from '../UI/LoadingSpinner';
import { CheckCircle, XCircle, AlertTriangle, RefreshCw, FileCheck } from 'lucide-react';
import { validatePreSubmission, type PreSubmissionReport } from '../../api/qaApi';
import { useToastStore } from '../../stores/toast';

export interface QAPreSubmissionPanelProps {
  reportId: string;
}

export function QAPreSubmissionPanel({ reportId }: QAPreSubmissionPanelProps) {
  const [isValidating, setIsValidating] = useState(false);
  const { addToast } = useToastStore();
  
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['qa-pre-submission', reportId],
    queryFn: () => validatePreSubmission(reportId),
    enabled: false, // Don't auto-fetch, require manual trigger
    retry: false
  });
  
  const validateMutation = useMutation({
    mutationFn: () => validatePreSubmission(reportId),
    onSuccess: () => {
      refetch();
    },
    onError: (error: any) => {
      console.error('[QA Pre-Submission] Validation failed:', error);
      const isTimeout = error?.code === 'ECONNABORTED' || error?.message?.includes('timeout');
      addToast({
        type: 'error',
        message: isTimeout
          ? 'Pre-submission validation timed out — the AI service is busy. Please try again in a moment.'
          : `Validation failed: ${error?.response?.data?.detail || error?.message || 'Service temporarily unavailable'}`,
        duration: 6000,
      });
    }
  });
  
  const handleValidate = async () => {
    setIsValidating(true);
    try {
      await validateMutation.mutateAsync();
    } finally {
      setIsValidating(false);
    }
  };
  
  if (!data && !isLoading && !error) {
    return (
      <Card>
        <div className="text-center py-8">
          <FileCheck className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Click "Run Pre-Submission Validation" to check all QA requirements
          </p>
          <Button
            onClick={handleValidate}
            disabled={isValidating}
            leftIcon={isValidating ? <LoadingSpinner /> : <FileCheck className="w-4 h-4" />}
          >
            {isValidating ? 'Validating...' : 'Run Pre-Submission Validation'}
          </Button>
        </div>
      </Card>
    );
  }
  
  if (isLoading || isValidating) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
          <span className="ml-3 text-gray-600 dark:text-gray-400">Running validation...</span>
        </div>
      </Card>
    );
  }
  
  if (error) {
    return (
      <Card>
        <div className="text-center py-8">
          <AlertTriangle className="h-12 w-12 mx-auto text-red-400 mb-4" />
          <p className="text-red-600 dark:text-red-400 mb-4">
            Failed to validate: {error instanceof Error ? error.message : 'Unknown error'}
          </p>
          <Button onClick={handleValidate} variant="outline">
            Retry
          </Button>
        </div>
      </Card>
    );
  }
  
  if (!data) {
    return null;
  }
  
  const report: PreSubmissionReport = data;
  
  return (
    <Card>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold mb-2">Pre-Submission Review</h3>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={report.overall_ready ? 'success' : 'error'} className="text-base px-3 py-1">
              {report.overall_ready ? 'Ready for Submission' : 'Not Ready'}
            </Badge>
            {report.generated_at && (
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Validated: {new Date(report.generated_at).toLocaleString()}
              </span>
            )}
          </div>
        </div>
        <Button
          onClick={handleValidate}
          disabled={isValidating}
          variant="outline"
          size="sm"
          leftIcon={isValidating ? <LoadingSpinner /> : <RefreshCw className="w-4 h-4" />}
        >
          {isValidating ? 'Validating...' : 'Re-validate'}
        </Button>
      </div>
      
      {/* Checklist Completion Summary */}
      <div className="mb-6 p-4 bg-theme-surface rounded-lg border border-theme-border">
        <h4 className="font-medium text-theme-text mb-3">Checklist Completion</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-gray-600 dark:text-gray-400">Writer Checklist</span>
              <Badge variant={report.checklist_completion.writer_checklist.completed ? 'success' : 'warning'}>
                {report.checklist_completion.writer_checklist.percentage.toFixed(0)}%
              </Badge>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-500">
              {report.checklist_completion.writer_checklist.completed_items} / {report.checklist_completion.writer_checklist.total_items} items
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-gray-600 dark:text-gray-400">Reviewer Checklist</span>
              <Badge variant={report.checklist_completion.reviewer_checklist.completed ? 'success' : 'warning'}>
                {report.checklist_completion.reviewer_checklist.percentage.toFixed(0)}%
              </Badge>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-500">
              {report.checklist_completion.reviewer_checklist.completed_items} / {report.checklist_completion.reviewer_checklist.total_items} items
            </div>
          </div>
        </div>
      </div>
      
      {/* Formatting Status */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <h4 className="font-medium text-theme-text">Formatting</h4>
          {report.formatting.overall_passed ? (
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
          ) : (
            <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
          )}
        </div>
        {report.formatting.fix_recommendations && report.formatting.fix_recommendations.length > 0 && (
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {report.formatting.fix_recommendations.length} issue{report.formatting.fix_recommendations.length !== 1 ? 's' : ''} found
          </div>
        )}
      </div>
      
      {/* Grammar Status */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <h4 className="font-medium text-theme-text">Grammar & Style</h4>
          {report.grammar.overall_passed ? (
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
          ) : (
            <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
          )}
        </div>
        {report.grammar.errors && report.grammar.errors.length > 0 && (
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {report.grammar.errors.filter(e => e.severity === 'error').length} error{report.grammar.errors.filter(e => e.severity === 'error').length !== 1 ? 's' : ''} found
          </div>
        )}
      </div>
      
      {/* Blocking Issues */}
      {report.blocking_issues.length > 0 && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
          <div className="flex items-start gap-2 mb-3">
            <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5" />
            <h4 className="font-medium text-red-800 dark:text-red-200">Blocking Issues</h4>
          </div>
          <ul className="list-disc list-inside text-sm text-red-700 dark:text-red-300 space-y-1">
            {report.blocking_issues.map((issue, idx) => (
              <li key={idx}>{issue}</li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Recommendations */}
      {report.recommendations.length > 0 && (
        <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
          <div className="flex items-start gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
            <h4 className="font-medium text-yellow-800 dark:text-yellow-200">Recommendations</h4>
          </div>
          <ul className="list-disc list-inside text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
            {report.recommendations.map((rec, idx) => (
              <li key={idx}>{rec}</li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Ready for Submission Indicator */}
      {report.overall_ready && (
        <div className="p-6 bg-green-50 dark:bg-green-900/20 rounded-lg border-2 border-green-500 dark:border-green-600 text-center">
          <CheckCircle className="h-16 w-16 mx-auto text-green-600 dark:text-green-400 mb-4" />
          <h4 className="text-xl font-bold text-green-800 dark:text-green-200 mb-2">
            Ready for Submission
          </h4>
          <p className="text-green-700 dark:text-green-300">
            All QA checks passed. This proposal is ready to be submitted.
          </p>
        </div>
      )}
    </Card>
  );
}
